import { useState, useEffect, FC, useRef } from "react";
import "../CSS/CalendarStrip.css";
import { format, startOfMonth, endOfMonth, eachDayOfInterval } from "date-fns";

interface CalendarStripProps {
  selectedDate: Date;
  setSelectedDate: (date: string) => void;
}

const CalendarStrip: FC<CalendarStripProps> = ({
  selectedDate,
  setSelectedDate,
}) => {
  const [monthDays, setMonthDays] = useState<Date[]>([]);
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledInitially = useRef<boolean>(false);
  
  useEffect(() => {
    // Get all days in the selected month
    const start = startOfMonth(selectedDate);
    const end = endOfMonth(selectedDate);
    const days = eachDayOfInterval({ start, end });
    setMonthDays(days);
  }, [selectedDate]);

  useEffect(() => {
    if (!hasScrolledInitially.current && selectedRef.current) {
      const parent = selectedRef.current.parentElement;
      if (parent && monthDays.length) {
        // Find index of selected date
        const selectedIndex = monthDays.findIndex(
          (d) => format(d, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd")
        );
        // If selected is one of last 4 days, scroll to right end
        if (selectedIndex >= monthDays.length - 4) {
          parent.scrollLeft = parent.scrollWidth - parent.clientWidth;
        } else {
          // Usual (left) scroll logic for all other days
          selectedRef.current.scrollIntoView({
            behavior: "auto",
            inline: "start",
            block: "nearest",
          });
          parent.scrollLeft =
            parent.scrollLeft - 24 > 0 ? parent.scrollLeft - 24 : 0;
        }
      }
      hasScrolledInitially.current = true;
    }
  }, [monthDays, selectedDate]);

  const handleSelect = (date: Date): void => {
    setSelectedDate(date.toISOString());
  };

  // Calculate 30-day window
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const endDate = new Date(today);
  endDate.setDate(today.getDate() + 29);

  return (
    <div className="calendar-strip">
      {monthDays.map((date: Date) => {
        const isSelected =
          format(date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");
        const isInWindow = date >= today && date <= endDate;
        return (
          <div
            key={date.toString()}
            className={`calendar-day ${isSelected ? "selected" : ""} ${
              !isInWindow ? "disabled" : ""
            }`}
            onClick={() => isInWindow && handleSelect(date)}
            ref={isSelected ? selectedRef : null}
            style={!isInWindow ? { pointerEvents: "none", opacity: 0.5 } : {}}
          >
            <div className="day-label">{format(date, "EEE").toUpperCase()}</div>
            <div className="day-number">{format(date, "d")}</div>
          </div>
        );
      })}
    </div>
  );
};

export default CalendarStrip;
